import http from 'hub-http/clients/apiClient';
import { List } from 'immutable';
import I18n from 'I18n';
import { RollupWarningMessage } from '../components/RollupWarningMessage';
import { fromSnowflakeProperty, getFieldLabel, getIconFromField } from './column-utils';
import { getReportFieldRefs } from './report-utils';
import { DataSourceTypes } from '../schema/source-records';
import { getDangerousRollupPropertyTableLabel, isDangerousRollupProperty, shouldHideProperty } from './source-utils';
import { getDataSourceId, getTableLabelWithSelfJoins } from './table-utils';
import { buildTargetPortalIdsQueryParam } from './multi-portal-query-params';
import { jsx as _jsx } from "react/jsx-runtime";
export const MAXIMUM_NUMBER_OF_VISIBLE_TOP_PROPERTIES = 6;
const getTopPropertiesUrl = (dataSourceId, targetPortalIds) => {
  const isMultiAccount = targetPortalIds && targetPortalIds.length > 0;
  const baseUrl = `sql-reporting/v1/extended-reportable-options/top-properties/${dataSourceId}`;
  const multiAccountUrl = `sql-reporting/v1/extended-reportable-options/multi-account/top-properties/${dataSourceId}`;
  if (isMultiAccount) {
    const queryParams = buildTargetPortalIdsQueryParam(targetPortalIds);
    return `${multiAccountUrl}?${queryParams}`;
  }
  return baseUrl;
};
const getTopPropertiesByDataSourceId = (dataSourceId, objectTypeName, targetPortalIds) => {
  const url = getTopPropertiesUrl(dataSourceId, targetPortalIds);
  return http.get(url).then(response => {
    return response.portalTopProperties.length > 0 ? {
      key: objectTypeName,
      value: response.portalTopProperties
    } : {
      key: objectTypeName,
      value: response.defaultTopProperties
    };
  });
};
export const buildTopPropertiesByObjectType = tableList => {
  const promises = tableList.filter(table => table.type === DataSourceTypes.HUBSPOT_OBJECT).map(table => getTopPropertiesByDataSourceId(getDataSourceId(table), table.name)).toArray();
  return Promise.all(promises);
};
export const buildTopPropertiesByObjectTypeMultiAccount = (tableList, targetPortalIds) => {
  if (!targetPortalIds || targetPortalIds.length === 0) {
    return buildTopPropertiesByObjectType(tableList);
  }
  const promises = tableList.filter(table => table.type === DataSourceTypes.VIRTUAL_DATASOURCE).map(table => {
    const dataSourceId = getDataSourceId(table);
    return getTopPropertiesByDataSourceId(dataSourceId, table.name, targetPortalIds);
  }).toArray();
  return Promise.all(promises);
};
const isHiddenPropertyUsedInReport = (reportFieldRefs, snowflakeProperty, tableName) => {
  return shouldHideProperty(snowflakeProperty) && reportFieldRefs.some(fieldRef => tableName === fieldRef.table && snowflakeProperty.name === fieldRef.property);
};
export const getTopPropertyData = ({
  tableDescription,
  properties,
  tables,
  persistedReport,
  topProperties,
  allJoinMetadata
}) => {
  const reportFieldRefs = persistedReport ? getReportFieldRefs(persistedReport.get('reportDefinition')) : List();
  return topProperties.filter((_, tableName) => {
    return properties.has(tableName);
  }).map((topPropertiesForTable, tableName) => {
    return topPropertiesForTable.map(topPropertyName => {
      return properties.getIn([tableName, topPropertyName]);
    }).filter(snowflakeProperty => snowflakeProperty && (!shouldHideProperty(snowflakeProperty) || isHiddenPropertyUsedInReport(reportFieldRefs, snowflakeProperty, tableName))).map(snowflakeProperty => {
      const field = fromSnowflakeProperty(snowflakeProperty, tableName);
      const text = getFieldLabel(field, snowflakeProperty);
      const icon = getIconFromField(field);
      const sourceLabel = getTableLabelWithSelfJoins(tableName, tableDescription, tables.get(tableName), allJoinMetadata);
      const warningMessage = isDangerousRollupProperty(tableDescription, snowflakeProperty) ? /*#__PURE__*/_jsx(RollupWarningMessage, {
        tableName: getDangerousRollupPropertyTableLabel(tableDescription, tables.toList(), snowflakeProperty)
      }) : undefined;
      const errorMessage = isHiddenPropertyUsedInReport(reportFieldRefs, snowflakeProperty, tableName) ? I18n.text('reporting-snowflake.metadata-errors.invalid-property') : undefined;
      const objectTypeId = tables.getIn([tableName, 'objectTypeId']);
      return {
        text,
        value: field,
        icon,
        sourceLabel,
        errorMessage,
        warningMessage,
        objectTypeId,
        isTopProperty: true,
        isFlpPermissioned: snowflakeProperty.flpRestricted
      };
    });
  });
};